/**
 * BrokerageResults — displays extracted holdings, summary, and activity
 * for brokerage jobs (those with metadata.subType === 'brokerage').
 *
 * Reads from GET /protected/job/:jobId/positions which returns
 * PositionLines + ActivityLines + per-(documentId, account) summary
 * fields collected from MetadataFields.
 *
 * Renders one of three views based on the `view` prop:
 *   - "summary"  : Account header card + period + summary table only
 *   - "holdings" : Positions grouped by section (the v1.0 default)
 *   - "activity" : Time-series transaction rows with action filter
 *
 * Backend contract: see api/src/handlers/jobs-handler.ts → getPositions().
 */

import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Spinner,
  Alert,
  Button,
  Form,
} from "react-bootstrap";
import { saveAs } from "file-saver";
import {
  getJobPositions,
  downloadJobPositionsExcel,
} from "../../../docuclipper/api";

type Position = {
  id: string;
  documentId: string;
  jobId: string;
  account: string;
  accountNickname: string;
  rowNumber: number;
  section:
    | "cash"
    | "money_market"
    | "bonds"
    | "equities"
    | "mutual_funds"
    | "options"
    | "other";
  symbol: string | null;
  cusip: string | null;
  description: string;
  quantity: number | null;
  price: number | null;
  marketValue: number;
  costBasis: number | null;
  unrealizedGainLoss: number | null;
  estAnnualIncome: number | null;
  estYieldPct: number | null;
  periodStart: string | null;
  periodEnd: string | null;
};

type ActivityAction =
  | "Buy"
  | "Sell"
  | "Dividend"
  | "Interest"
  | "Deposit"
  | "Withdrawal"
  | "Fee"
  | "Transfer"
  | "Reinvest"
  | "Other";

type Activity = {
  id: string;
  documentId: string;
  jobId: string;
  account: string;
  accountNickname: string;
  rowNumber: number;
  date: string | null;
  settleDate: string | null;
  action: ActivityAction;
  actionRaw: string;
  symbol: string | null;
  cusip: string | null;
  description: string;
  quantity: number | null;
  price: number | null;
  amount: number;
};

type HoldingPeriod = "short" | "long" | "unknown";

type RealizedLot = {
  id: string;
  documentId: string;
  jobId: string;
  account: string;
  accountNickname: string;
  rowNumber: number;
  symbol: string | null;
  cusip: string | null;
  description: string;
  quantity: number;
  dateAcquired: string | null;
  dateSold: string | null;
  costBasis: number;
  proceeds: number;
  gainLoss: number;
  holdingPeriod: HoldingPeriod;
  isWashSale: boolean;
};

type Summary = {
  documentId: string;
  account: string;
  beginningValue?: string;
  endingValue?: string;
  changeThisPeriod?: string;
  depositsPeriod?: string;
  withdrawalsPeriod?: string;
  dividendsInterestPeriod?: string;
  startDate?: string;
  endDate?: string;
  accountNumber?: string;
  broker?: string;
  accountType?: string;
  statementVariant?: string;
  isReconciled?: string;
};

type ApiResponse = {
  positions: Position[];
  activity?: Activity[]; // optional for backwards compat with v1.0 deploys
  realizedLots?: RealizedLot[]; // optional for backwards compat with pre-v1.3 deploys
  summaries: Summary[];
};

const fmtMoney = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtPct = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return "—";
  return `${v.toFixed(2)}%`;
};

const SECTION_LABEL: Record<Position["section"], string> = {
  cash: "Cash",
  money_market: "Money Market",
  bonds: "Bonds / CDs",
  equities: "Equities",
  mutual_funds: "Mutual Funds",
  options: "Options",
  other: "Other",
};

const SECTION_ORDER: Position["section"][] = [
  "cash",
  "money_market",
  "bonds",
  "equities",
  "mutual_funds",
  "options",
  "other",
];

const ACTION_BADGE: Record<ActivityAction, string> = {
  Buy: "primary",
  Sell: "primary",
  Dividend: "success",
  Interest: "success",
  Deposit: "info",
  Withdrawal: "warning",
  Fee: "secondary",
  Transfer: "info",
  Reinvest: "success",
  Other: "secondary",
};

interface BrokerageResultsProps {
  jobId: number;
  view?: "summary" | "holdings" | "activity" | "realized";
  // When the parent (project workspace) renders the Download Excel action in
  // its own toolbar, suppress the inline button so it doesn't appear twice.
  hideDownloadButton?: boolean;
}

export const BrokerageResults: React.FC<BrokerageResultsProps> = ({
  jobId,
  view = "holdings",
  hideDownloadButton = false,
}) => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getJobPositions(jobId)
      .then((rsp: ApiResponse) => {
        if (cancelled) return;
        setData(rsp);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || "Failed to load positions");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" /> Loading brokerage data…
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">Error loading positions: {error}</Alert>;
  }

  // Group positions + activity + realizedLots + summaries by (documentId, account)
  const positionsByDocAccount = new Map<string, Position[]>();
  const activityByDocAccount = new Map<string, Activity[]>();
  const realizedByDocAccount = new Map<string, RealizedLot[]>();
  const summariesByKey = new Map<string, Summary>();
  if (data) {
    for (const p of data.positions) {
      const key = `${p.documentId}|${p.account}`;
      if (!positionsByDocAccount.has(key)) positionsByDocAccount.set(key, []);
      positionsByDocAccount.get(key)!.push(p);
    }
    for (const a of data.activity || []) {
      const key = `${a.documentId}|${a.account}`;
      if (!activityByDocAccount.has(key)) activityByDocAccount.set(key, []);
      activityByDocAccount.get(key)!.push(a);
    }
    for (const r of data.realizedLots || []) {
      const key = `${r.documentId}|${r.account}`;
      if (!realizedByDocAccount.has(key)) realizedByDocAccount.set(key, []);
      realizedByDocAccount.get(key)!.push(r);
    }
    for (const s of data.summaries) {
      summariesByKey.set(`${s.documentId}|${s.account}`, s);
    }
  }

  const handleDownloadExcel = async () => {
    try {
      const blob = await downloadJobPositionsExcel(jobId);
      saveAs(blob, `brokerage-job-${jobId}.xlsx`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const downloadButton = hideDownloadButton ? null : (
    <div className="d-flex justify-content-end mb-3">
      <Button
        variant="primary"
        size="sm"
        data-cy="download-positions-excel"
        onClick={handleDownloadExcel}
      >
        Download Excel
      </Button>
    </div>
  );

  // Empty-state messaging is per-view: holdings view falls back to the
  // legacy "no positions extracted" copy; activity view says "no activity";
  // realized view says "no realized gains"; summary view always renders if
  // any summary exists.
  if (!data || (view === "holdings" && data.positions.length === 0)) {
    return (
      <Alert variant="warning">
        No positions extracted for this brokerage job. The extractor may not
        yet recognize this broker, or the statement format may need additional
        regex coverage. Currently supported: Schwab, Fidelity, E*TRADE,
        Morgan Stanley, Merrill, Wells Fargo Advisors, Raymond James,
        TD Ameritrade, Edward Jones.
      </Alert>
    );
  }

  // ── SUMMARY view ───────────────────────────────────────────────────
  if (view === "summary") {
    if (data.summaries.length === 0) {
      return <Alert variant="warning">No summary data extracted yet.</Alert>;
    }
    return (
      <div data-cy="brokerage-summary">
        {downloadButton}
        {data.summaries.map((s) => {
          const key = `${s.documentId}|${s.account}`;
          const reconciled = s.isReconciled === "true";
          return (
            <Card key={key} className="mb-4">
              <Card.Header>
                <strong>
                  {s.broker?.toUpperCase() || "Brokerage"}{" "}
                  {s.accountNumber || "Account"}
                </strong>
                {s.startDate && s.endDate && (
                  <span className="ml-3 text-muted">
                    {s.startDate} to {s.endDate}
                  </span>
                )}
                <span
                  className={`ml-3 badge ${
                    reconciled ? "badge-success" : "badge-warning"
                  }`}
                >
                  {reconciled ? "Reconciled" : "Not reconciled"}
                </span>
              </Card.Header>
              <Card.Body>
                <Table size="sm" borderless responsive>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Beginning Value</strong>
                      </td>
                      <td>{fmtMoney(s.beginningValue)}</td>
                      <td>
                        <strong>Ending Value</strong>
                      </td>
                      <td>{fmtMoney(s.endingValue)}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Change (period)</strong>
                      </td>
                      <td>{fmtMoney(s.changeThisPeriod)}</td>
                      <td>
                        <strong>Dividends / Interest</strong>
                      </td>
                      <td>{fmtMoney(s.dividendsInterestPeriod)}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Deposits</strong>
                      </td>
                      <td>{fmtMoney(s.depositsPeriod)}</td>
                      <td>
                        <strong>Withdrawals</strong>
                      </td>
                      <td>{fmtMoney(s.withdrawalsPeriod)}</td>
                    </tr>
                    {s.accountType && (
                      <tr>
                        <td>
                          <strong>Account Type</strong>
                        </td>
                        <td colSpan={3}>{s.accountType}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  // ── ACTIVITY view ──────────────────────────────────────────────────
  if (view === "activity") {
    const allActivity = data.activity || [];
    if (allActivity.length === 0) {
      return (
        <Alert variant="warning">
          No activity extracted for this brokerage job. The activity parser
          may not yet support this broker (Schwab + Fidelity + E*TRADE
          coverage shipped in v1.1).
        </Alert>
      );
    }
    const filtered = actionFilter
      ? allActivity.filter((a) => a.action === actionFilter)
      : allActivity;
    const actionsPresent = Array.from(
      new Set(allActivity.map((a) => a.action)),
    ).sort();
    return (
      <div data-cy="brokerage-activity">
        {downloadButton}
        <div className="mb-3">
          <Form.Group controlId="activity-action-filter" className="mb-0">
            <Form.Label className="mr-2">Filter by action:</Form.Label>
            <Form.Control
              as="select"
              size="sm"
              style={{ display: "inline-block", width: "auto" }}
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              data-cy="activity-action-filter"
            >
              <option value="">All actions</option>
              {actionsPresent.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Form.Control>
            <span className="ml-3 text-muted">
              {filtered.length} of {allActivity.length} rows
            </span>
          </Form.Group>
        </div>
        {Array.from(activityByDocAccount.entries()).map(([key, acts]) => {
          const filteredForDoc = actionFilter
            ? acts.filter((a) => a.action === actionFilter)
            : acts;
          if (filteredForDoc.length === 0) return null;
          const summary = summariesByKey.get(key);
          return (
            <Card key={key} className="mb-4">
              <Card.Header>
                <strong>
                  {summary?.broker?.toUpperCase() || "Brokerage"}{" "}
                  {summary?.accountNumber || "Account"}
                </strong>
                {summary?.startDate && summary?.endDate && (
                  <span className="ml-3 text-muted">
                    {summary.startDate} to {summary.endDate}
                  </span>
                )}
              </Card.Header>
              <Card.Body>
                <Table size="sm" striped responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Symbol</th>
                      <th>Description</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Price</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredForDoc.map((a) => (
                      <tr key={a.id}>
                        <td>{a.date || "—"}</td>
                        <td>
                          <span
                            className={`badge badge-${ACTION_BADGE[a.action]}`}
                            title={a.actionRaw}
                          >
                            {a.action}
                          </span>
                        </td>
                        <td>
                          <code>{a.symbol || "—"}</code>
                        </td>
                        <td>{a.description}</td>
                        <td className="text-right">
                          {a.quantity !== null
                            ? a.quantity.toLocaleString()
                            : "—"}
                        </td>
                        <td className="text-right">
                          {a.price !== null ? fmtMoney(a.price) : "—"}
                        </td>
                        <td
                          className={`text-right ${
                            a.amount >= 0 ? "text-success" : "text-danger"
                          }`}
                        >
                          <strong>{fmtMoney(a.amount)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  // ── REALIZED GAINS view ───────────────────────────────────────────
  if (view === "realized") {
    const allRealized = data.realizedLots || [];
    if (allRealized.length === 0) {
      return (
        <Alert variant="info" data-cy="brokerage-realized-empty">
          No realized gains extracted (only year-end and advisor-managed
          statements typically include realized gains data).
        </Alert>
      );
    }
    const totalGainLoss = allRealized.reduce(
      (sum, r) => sum + (r.gainLoss || 0),
      0,
    );
    return (
      <div data-cy="brokerage-realized">
        {downloadButton}
        <div className="mb-3">
          <strong>Total realized gain/loss: </strong>
          <span
            className={
              totalGainLoss >= 0 ? "text-success" : "text-danger"
            }
          >
            <strong>{fmtMoney(totalGainLoss)}</strong>
          </span>
          <span className="ml-3 text-muted">
            {allRealized.length}{" "}
            {allRealized.length === 1 ? "disposition" : "dispositions"}
          </span>
        </div>
        {Array.from(realizedByDocAccount.entries()).map(([key, lots]) => {
          const summary = summariesByKey.get(key);
          // Sort by dateSold desc (most recent first); rows without dateSold trail
          const sorted = [...lots].sort((a, b) => {
            if (a.dateSold && b.dateSold) {
              return b.dateSold.localeCompare(a.dateSold);
            }
            if (a.dateSold) return -1;
            if (b.dateSold) return 1;
            return 0;
          });
          return (
            <Card key={key} className="mb-4">
              <Card.Header>
                <strong>
                  {summary?.broker?.toUpperCase() || "Brokerage"}{" "}
                  {summary?.accountNumber || "Account"}
                </strong>
                {summary?.startDate && summary?.endDate && (
                  <span className="ml-3 text-muted">
                    {summary.startDate} to {summary.endDate}
                  </span>
                )}
              </Card.Header>
              <Card.Body>
                <Table size="sm" striped responsive>
                  <thead>
                    <tr>
                      <th>Date Sold</th>
                      <th>Symbol</th>
                      <th>Description</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Cost Basis</th>
                      <th className="text-right">Proceeds</th>
                      <th className="text-right">Gain / Loss</th>
                      <th>Holding Period</th>
                      <th>Wash Sale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r) => (
                      <tr key={r.id}>
                        <td>{r.dateSold || "—"}</td>
                        <td>
                          <code>{r.symbol || "—"}</code>
                        </td>
                        <td>{r.description}</td>
                        <td className="text-right">
                          {r.quantity.toLocaleString()}
                        </td>
                        <td className="text-right">{fmtMoney(r.costBasis)}</td>
                        <td className="text-right">{fmtMoney(r.proceeds)}</td>
                        <td
                          className={`text-right ${
                            r.gainLoss >= 0 ? "text-success" : "text-danger"
                          }`}
                        >
                          <strong>{fmtMoney(r.gainLoss)}</strong>
                        </td>
                        <td>
                          <span
                            className={`badge badge-${
                              r.holdingPeriod === "long"
                                ? "info"
                                : r.holdingPeriod === "short"
                                  ? "warning"
                                  : "secondary"
                            }`}
                          >
                            {r.holdingPeriod}
                          </span>
                        </td>
                        <td>
                          {r.isWashSale ? (
                            <span className="badge badge-danger">W</span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  // ── HOLDINGS view (default) ────────────────────────────────────────
  return (
    <div data-cy="brokerage-holdings">
      {downloadButton}
      {Array.from(positionsByDocAccount.entries()).map(([key, positions]) => {
        const summary = summariesByKey.get(key);
        const reconciled = summary?.isReconciled === "true";
        return (
          <Card key={key} className="mb-4">
            <Card.Header>
              <strong>
                {summary?.broker?.toUpperCase() || "Brokerage"}{" "}
                {summary?.accountNumber || "Account"}
              </strong>
              {summary?.startDate && summary?.endDate && (
                <span className="ml-3 text-muted">
                  {summary.startDate} to {summary.endDate}
                </span>
              )}
              <span
                className={`ml-3 badge ${
                  reconciled ? "badge-success" : "badge-warning"
                }`}
              >
                {reconciled ? "Reconciled" : "Not reconciled"}
              </span>
            </Card.Header>
            <Card.Body>
              {/* Group positions by section */}
              {SECTION_ORDER.map((section) => {
                const sectionPositions = positions.filter(
                  (p) => p.section === section,
                );
                if (sectionPositions.length === 0) return null;
                const sectionTotal = sectionPositions.reduce(
                  (sum, p) => sum + (p.marketValue || 0),
                  0,
                );
                return (
                  <div key={section} className="mb-3">
                    <h6>
                      {SECTION_LABEL[section]}{" "}
                      <span className="text-muted">
                        ({sectionPositions.length}{" "}
                        {sectionPositions.length === 1
                          ? "position"
                          : "positions"}
                        , {fmtMoney(sectionTotal)})
                      </span>
                    </h6>
                    <Table size="sm" striped responsive>
                      <thead>
                        <tr>
                          <th>Symbol</th>
                          <th>Description</th>
                          <th className="text-right">Quantity</th>
                          <th className="text-right">Price</th>
                          <th className="text-right">Market Value</th>
                          <th className="text-right">Cost Basis</th>
                          <th className="text-right">Unrealized G/L</th>
                          <th className="text-right">Yield</th>
                          <th className="text-right">EAI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sectionPositions.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <code>{p.symbol || "—"}</code>
                            </td>
                            <td>{p.description}</td>
                            <td className="text-right">
                              {p.quantity?.toLocaleString() ?? "—"}
                            </td>
                            <td className="text-right">
                              {p.price !== null ? fmtMoney(p.price) : "—"}
                            </td>
                            <td className="text-right">
                              <strong>{fmtMoney(p.marketValue)}</strong>
                            </td>
                            <td className="text-right">
                              {fmtMoney(p.costBasis)}
                            </td>
                            <td
                              className={`text-right ${
                                p.unrealizedGainLoss != null
                                  ? p.unrealizedGainLoss >= 0
                                    ? "text-success"
                                    : "text-danger"
                                  : ""
                              }`}
                            >
                              {fmtMoney(p.unrealizedGainLoss)}
                            </td>
                            <td className="text-right">
                              {fmtPct(p.estYieldPct)}
                            </td>
                            <td className="text-right">
                              {fmtMoney(p.estAnnualIncome)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

/**
 * Helper: parse Job.metadata (which is stored as JSON-serialized string)
 * and return the subType. Returns null if not parseable or not a brokerage.
 */
export function getJobSubType(
  metadataJson: string | null | undefined,
): string | null {
  if (!metadataJson) return null;
  try {
    const parsed = JSON.parse(metadataJson);
    return parsed?.subType ?? null;
  } catch {
    return null;
  }
}

export function isBrokerageJob(
  metadataJson: string | null | undefined,
): boolean {
  return getJobSubType(metadataJson) === "brokerage";
}

